import { proxyCustomElement, HTMLElement, createEvent, h } from '@stencil/core/internal/client';
import { c as create$3, F as FormController } from './form.js';
import { a as BusinessFormStep, B as BusinessFormStepV2 } from './business-form-types.js';
import { g as bankNameValidation, n as nicknameValidation, i as identityNameValidation, h as accountTypeValidation, j as accountNumberValidation, k as routingNumberValidation } from './schema-validations.js';
import { c as bankAccountTypeOptions } from './business-form-options.js';
import { A as Api } from './Api.js';
import './Pagination.js';
import './Payment.js';
import './Payout.js';
import { c as config } from './config.js';
import { n as numberOnlyHandler } from './utils4.js';
import { C as ComponentErrorCodes, a as ComponentErrorSeverity } from './ComponentError.js';
import { d as defineCustomElement$5 } from './custom-popper2.js';
import { d as defineCustomElement$4 } from './form-control-error-text2.js';
import { d as defineCustomElement$3 } from './form-control-select2.js';
import { d as defineCustomElement$2 } from './form-control-text2.js';
import { d as defineCustomElement$1 } from './form-control-tooltip2.js';

const businessBankAccountSchema = (allowOptionalFields) => {
    const schema = create$3({
        bank_name: bankNameValidation.required('Enter bank name'),
        nickname: nicknameValidation.required('Enter nickname'),
        account_owner_name: identityNameValidation.required('Enter account owner name'),
        account_type: accountTypeValidation.required('Select account type'),
        account_number: accountNumberValidation.required('Enter account number'),
        routing_number: routingNumberValidation.required('Enter routing number')
    });
    const easySchema = create$3({
        bank_name: bankNameValidation.nullable(),
        nickname: nicknameValidation.nullable(),
        account_owner_name: identityNameValidation.nullable(),
        account_type: accountTypeValidation.nullable(),
        account_number: accountNumberValidation.nullable(),
        routing_number: routingNumberValidation.nullable()
    });
    return allowOptionalFields ? easySchema : schema;
};

class BankAccount {
    constructor(data) {
        this.id = data.id;
        this.account_owner_name = data.account_owner_name;
        this.full_name = data.full_name;
        this.account_type = data.account_type;
        this.acct_last_four = data.acct_last_four;
        this.account_number_last4 = data.account_number_last4;
        this.routing_number = data.routing_number;
        this.bank_name = data.bank_name;
        this.country = data.country;
        this.currency = data.currency;
        this.nickname = data.nickname;
        this.metadata = data.metadata;
        this.business_id = data.business_id;
        this.platform_account_id = data.platform_account_id;
        this.created_at = data.created_at;
        this.updated_at = data.updated_at;
    }
}

const BusinessBankAccountFormStep = /*@__PURE__*/ proxyCustomElement(class BusinessBankAccountFormStep extends HTMLElement {
    constructor() {
        super();
        this.__registerHost();
        this.submitted = createEvent(this, "submitted", 7);
        this.stepCompleted = createEvent(this, "form-step-completed", 7);
        this.formLoading = createEvent(this, "formLoading", 7);
        this.errorEvent = createEvent(this, "error-event", 7);
        this.fetchData = async () => {
            this.formLoading.emit(true);
            try {
                const response = await this.api.get(this.businessEndpoint);
                if (response.data.bank_accounts.length > 0) {
                    this.bankAccount = Object.assign({}, new BankAccount(response.data.bank_accounts[0]));
                }
                else {
                    this.bankAccount = Object.assign({}, new BankAccount({}));
                }
                this.formController.setInitialValues(Object.assign({}, this.bankAccount));
            }
            catch (error) {
                this.errorEvent.emit({
                    errorCode: ComponentErrorCodes.FETCH_ERROR,
                    message: error.message,
                    severity: ComponentErrorSeverity.ERROR,
                    data: error,
                });
            }
            finally {
                this.formLoading.emit(false);
            }
        };
        this.sendData = async (onSuccess) => {
            this.formLoading.emit(true);
            try {
                const formValues = this.formController.values.getValue();
                const payload = Object.assign(Object.assign({}, formValues), { business_id: this.businessId });
                const response = await this.api.post(this.bankAccountEndpoint, payload);
                this.handleResponse(response, onSuccess);
            }
            catch (error) {
                this.errorEvent.emit({
                    errorCode: ComponentErrorCodes.POST_ERROR,
                    message: error.message,
                    severity: ComponentErrorSeverity.ERROR,
                    data: error,
                });
            }
            finally {
                this.formLoading.emit(false);
            }
        };
        this.inputHandler = (name, value) => {
            this.formController.setValues(Object.assign(Object.assign({}, this.formController.values.getValue()), { [name]: value }));
        };
        this.formController = undefined;
        this.errors = {};
        this.bankAccount = undefined;
        this.authToken = undefined;
        this.businessId = undefined;
        this.allowOptionalFields = undefined;
    }
    get businessEndpoint() {
        return `entities/business/${this.businessId}`;
    }
    get bankAccountEndpoint() {
        return `entities/bank_accounts`;
    }
    get formDisabled() {
        var _a;
        return !!((_a = this.bankAccount) === null || _a === void 0 ? void 0 : _a.id);
    }
    handleResponse(response, onSuccess) {
        if (response.error) {
            this.errorEvent.emit({
                errorCode: ComponentErrorCodes.POST_ERROR,
                message: response.error.message,
                severity: ComponentErrorSeverity.ERROR,
                data: response.error,
            });
        }
        else {
            onSuccess();
        }
        this.submitted.emit({ data: response, metadata: { completedStep: BusinessFormStep.bankAccount } });
        this.stepCompleted.emit({ data: response, formStep: BusinessFormStepV2.bankAccount });
    }
    async validateAndSubmit({ onSuccess }) {
        if (this.formDisabled) {
            this.stepCompleted.emit({ data: null, formStep: BusinessFormStepV2.bankAccount, metadata: 'no data submitted' });
            onSuccess();
        }
        else {
            this.formController.validateAndSubmit(() => this.sendData(onSuccess));
        }
    }
    ;
    componentWillLoad() {
        this.formController = new FormController(businessBankAccountSchema(this.allowOptionalFields));
        this.api = Api({ authToken: this.authToken, apiOrigin: config.proxyApiOrigin });
        if (this.businessId && this.authToken) {
            this.fetchData();
        }
    }
    componentDidLoad() {
        this.formController.values.subscribe(values => this.bankAccount = Object.assign({}, values));
        this.formController.errors.subscribe(errors => {
            this.errors = Object.assign({}, errors);
        });
    }
    render() {
        const bankAccountDefaultValue = this.formController.getInitialValues();
        return (h("form", { key: 'c19f52934f1fa591d71175d1a0a584c0e9d0bd92' }, h("fieldset", { key: '96c326422e38efe3413b282fcc2415355108992c' }, h("div", { key: '7a39297bc087e310ad28fbdd58e71b509c002132', class: "d-flex align-items-center gap-2" }, h("legend", { key: '81b0cc0fc84846e04f3efb1efc0c33c181a3399d', class: "mb-0" }, "Bank Account Info"), h("form-control-tooltip", { key: 'f24f0605251a59c7df3a01bfb5d01302e95b45ba', helpText: "The Direct Deposit Account is the business bank account where your funds will be deposited. The name of this account must match the registered business name exactly. We are not able to accept personal accounts unless your business is a registered sole proprietorship." })), h("hr", { key: '8386d2b17e191ca529a663ced0c430e71c3da4d6', class: "mt-2" }), h("div", { key: 'd5ddcc4c0b54320a0e1f1fd381dd41e8bdb86c2f', class: "row gy-3" }, h("div", { key: '29bd4e26bed9fef74ee79241d5dc6fd9a4d5954e', class: "col-12" }, h("form-control-text", { key: 'ac9781048b7dfb24da9565b058994ffd78053db9', name: "bank_name", label: "Bank Name", defaultValue: bankAccountDefaultValue.bank_name, errorText: this.errors.bank_name, inputHandler: this.inputHandler, disabled: this.formDisabled })), h("div", { key: 'db9d13b0a62927b790bdcf5bc77ffa4fbadb227b', class: "col-12" }, h("form-control-text", { key: '41d192f0d9df09e62aa929642ca2ae40946f951e', name: "nickname", label: "Nickname", defaultValue: bankAccountDefaultValue.nickname, errorText: this.errors.nickname, inputHandler: this.inputHandler, disabled: this.formDisabled })), h("div", { key: '2b37a20c6bb6f942ccf5b49e84a2d6d1669d9fd7', class: "col-12" }, h("form-control-text", { key: '0392b8d08a48ff27af80d6fa42959b90a713de4e', name: "account_owner_name", label: "Account Owner Name", defaultValue: bankAccountDefaultValue.account_owner_name, errorText: this.errors.account_owner_name, inputHandler: this.inputHandler, disabled: this.formDisabled })), h("div", { key: '2a30485d70f0825e7e677b3f4b75caf4c601090e', class: "col-12" }, h("form-control-select", { key: '65cb43b080e47208bf83099cfec12cc8351281c6', name: "account_type", label: "Account Type", options: bankAccountTypeOptions, defaultValue: bankAccountDefaultValue.account_type, errorText: this.errors.account_type, inputHandler: this.inputHandler, disabled: this.formDisabled })), h("div", { key: 'ab0c65f348486814ac14ed548ecaa25610e14d58', class: "col-12" }, h("form-control-text", { key: '3c08ce88f652bfe6ae64807beab30ed796cad6c3', name: "account_number", label: "Account Number", defaultValue: bankAccountDefaultValue.account_number, maxLength: 17, errorText: this.errors.account_number, inputHandler: this.inputHandler, keyDownHandler: numberOnlyHandler, disabled: this.formDisabled, helpText: "Please copy the account number as shown on your statement/check. Do not include spaces or dashes." })), h("div", { key: 'f5314db551f6d4482f151b49d9db685b78223f2a', class: "col-12" }, h("form-control-text", { key: '1f204b3140c9fc4fe20519ebc082751c291ba816', name: "routing_number", label: "Routing Number", defaultValue: bankAccountDefaultValue.routing_number, maxLength: 9, errorText: this.errors.routing_number, inputHandler: this.inputHandler, keyDownHandler: numberOnlyHandler, disabled: this.formDisabled, helpText: "A valid routing number is nine digits. Please include any leading or trailing zeroes." }))))));
    }
}, [0, "justifi-business-bank-account-form-step", {
        "authToken": [1, "auth-token"],
        "businessId": [1, "business-id"],
        "allowOptionalFields": [4, "allow-optional-fields"],
        "formController": [32],
        "errors": [32],
        "bankAccount": [32],
        "validateAndSubmit": [64]
    }]);
function defineCustomElement() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["justifi-business-bank-account-form-step", "custom-popper", "form-control-error-text", "form-control-select", "form-control-text", "form-control-tooltip"];
    components.forEach(tagName => { switch (tagName) {
        case "justifi-business-bank-account-form-step":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, BusinessBankAccountFormStep);
            }
            break;
        case "custom-popper":
            if (!customElements.get(tagName)) {
                defineCustomElement$5();
            }
            break;
        case "form-control-error-text":
            if (!customElements.get(tagName)) {
                defineCustomElement$4();
            }
            break;
        case "form-control-select":
            if (!customElements.get(tagName)) {
                defineCustomElement$3();
            }
            break;
        case "form-control-text":
            if (!customElements.get(tagName)) {
                defineCustomElement$2();
            }
            break;
        case "form-control-tooltip":
            if (!customElements.get(tagName)) {
                defineCustomElement$1();
            }
            break;
    } });
}
defineCustomElement();

export { BusinessBankAccountFormStep as B, defineCustomElement as d };
